import { Button, Group, ScrollArea, Table } from '@mantine/core';
import { IconPencilPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundMessage } from '../../components/DataTable';
import { LoadingRows } from '../../components/LoadingRows';
import { PageHeader } from '../../components/PageHeader';
import { Paper } from '../../components/Paper';
import { SearchField } from '../../components/SearchField';
import { Title } from '../../components/Title';
import { useModal } from '../../hooks/useModal';
import {
	plainTableDefaults,
	useResponsiveSpace,
} from '../../hooks/useResponsiveSpace';
import { whiteIconButtonStyle } from '../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { query } from '../../util/api/query';
import { GroupRow } from './components/GroupRow';
import { NewGroupModal } from './components/NewGroupModal';
import type { GroupDto } from './types/GroupDto';
import { KeyedRow } from '../../components/KeyedRow';

export function Groups() {
	const { organisation } = useParams();

	const groupModal = useModal();

	const [searchQuery, setSearchQuery] = useState('');

	const groupsQuery = query<GroupDto[]>(
		`${organisation}/groups/search-groups?searchValue=${searchQuery}`,
	);

	const isDataFound = useMemo(() => {
		return groupsQuery.data?.value && groupsQuery.data?.value.length > 0;
	}, [groupsQuery.data?.value]);

	const responsiveHeight = useResponsiveSpace(plainTableDefaults);

	return (
		<>
			<NewGroupModal
				isOpened={groupModal.isOpen}
				close={groupModal.close}
				triggerListUpdate={() => groupsQuery.remove()}
			/>
			<PageHeader title={'Groups'} hideFiltering>
				<Group>
					<SearchField
						placeholder={'Search groups...'}
						onDelayComplete={(value) => setSearchQuery(value)}
					/>
					<Button {...whiteIconButtonStyle} onClick={() => groupModal.open()}>
						<IconPencilPlus {...defaultIconStyle} />
					</Button>
				</Group>
			</PageHeader>
			<Paper withPadding>
				<ScrollArea w={'100%'} h={responsiveHeight}>
					<Table stickyHeader striped={isDataFound}>
						<Table.Thead>
							<KeyedRow>
								<Table.Th miw={200}>
									<Title label={'Name'} />
								</Table.Th>
								<Table.Th w={'10vw'}>
									<Group justify={'center'}>
										<Title label={'Departments'} />
									</Group>
								</Table.Th>
								<Table.Th w={'4vw'} />
							</KeyedRow>
						</Table.Thead>
						<Table.Tbody>
							{groupsQuery.isLoading || groupsQuery.isRefetching ? (
								<LoadingRows rows={30} />
							) : (
								<>
									{isDataFound ? (
										<>
											{groupsQuery.data?.value.map((group) => (
												<GroupRow
													key={group.id}
													group={group}
													triggerListUpdate={() => {
														groupsQuery.remove();
														groupsQuery.refetch();
													}}
												/>
											))}
										</>
									) : (
										<KeyedRow>
											<Table.Td colSpan={4}>
												<NoDataFoundMessage />
											</Table.Td>
										</KeyedRow>
									)}
								</>
							)}
						</Table.Tbody>
					</Table>
				</ScrollArea>
			</Paper>
		</>
	);
}
